import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';

import { ActiveCoverageOption, filterCoverageOptions } from '@lib/insurance-packages/data-access';
import { ClinicClassSummaryComponent, CoverageCategoriesComponent, CoverageLimitsComponent, DiagnosesCountBadgeComponent } from '@lib/insurance-packages/ui';
import {
  fetchMemberActiveCoverage,
  fetchMemberCoverageOptionDiagnoses,
  selectMemberActiveCoverage,
  setMemberActiveCoverageSearch,
  toggleMemberActiveCoverageCategories,
  toggleMemberActiveCoverageOption,
} from '@lib/members/data-access';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { LoaderComponent } from '@lib/shared-ui/components/layout';

import { PackageCoverageDiagnosesComponent } from '../package-coverage-diagnoses/package-coverage-diagnoses.component';

@Component({
  selector: 'members-package-coverage',
  standalone: true,
  imports: [
    MatIcon,
    MatTooltip,
    TranslatePipe,
    LoaderComponent,
    PackageCoverageDiagnosesComponent,
    CoverageLimitsComponent,
    ClinicClassSummaryComponent,
    CoverageCategoriesComponent,
    DiagnosesCountBadgeComponent,
  ],
  templateUrl: './package-coverage.component.html',
  host: { class: 'flex min-h-0 flex-col' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PackageCoverageComponent implements OnInit {
  private readonly store = inject(Store);

  readonly userId = input.required<string>();
  readonly fillHeight = input<boolean>(false);

  private readonly activeCoverage = this.store.selectSignal(selectMemberActiveCoverage);
  private readonly currentCoverage = computed(() => {
    const coverage = this.activeCoverage().coverage;
    return coverage?.userId === this.userId() ? coverage : null;
  });

  protected readonly loading = computed<boolean>(() => this.currentCoverage()?.loading ?? true);
  protected readonly error = computed<boolean>(() => this.currentCoverage()?.error ?? false);
  protected readonly coverage = computed(() => this.currentCoverage()?.data ?? null);

  protected readonly search = computed<string>(() => this.activeCoverage().search);

  protected readonly diagnosesOption = signal<ActiveCoverageOption | null>(null);
  protected readonly diagnosesSearch = signal<string>('');
  private readonly currentDiagnoses = computed(() => {
    const optionId = this.diagnosesOption()?.id;
    return optionId ? (this.activeCoverage().optionDiagnoses[optionId] ?? null) : null;
  });
  protected readonly diagnosesLoading = computed<boolean>(() => this.currentDiagnoses()?.loading ?? true);
  protected readonly diagnosesError = computed<boolean>(() => this.currentDiagnoses()?.error ?? false);
  protected readonly diagnoses = computed<string[]>(() => this.currentDiagnoses()?.data ?? []);

  protected readonly visibleOptions = computed<ActiveCoverageOption[]>(() => filterCoverageOptions(this.coverage()?.options ?? [], this.search()));

  ngOnInit(): void {
    const coverage = this.currentCoverage();
    if (!coverage || coverage.error) this.loadCoverage();
  }

  protected loadCoverage(): void {
    this.store.dispatch(fetchMemberActiveCoverage({ userId: this.userId() }));
  }

  protected openDiagnoses(option: ActiveCoverageOption): void {
    this.diagnosesOption.set(option);
    this.diagnosesSearch.set('');
    const diagnoses = this.currentDiagnoses();
    if (!diagnoses || diagnoses.error) this.loadDiagnoses();
  }

  protected loadDiagnoses(): void {
    const coverageId = this.coverage()?.id;
    const optionId = this.diagnosesOption()?.id;
    if (coverageId && optionId) this.store.dispatch(fetchMemberCoverageOptionDiagnoses({ coverageId, optionId }));
  }

  protected closeDiagnoses(): void {
    this.diagnosesOption.set(null);
  }

  protected onSearch(search: string): void {
    this.store.dispatch(setMemberActiveCoverageSearch({ search }));
  }

  protected isOptionCollapsed(id: string): boolean {
    return !this.search().trim() && this.activeCoverage().collapsedOptions[id] === true;
  }

  protected toggleOption(id: string): void {
    this.store.dispatch(toggleMemberActiveCoverageOption({ optionId: id }));
  }

  protected areCategoriesExpanded(id: string): boolean {
    return this.activeCoverage().expandedCategories[id] === true;
  }

  protected toggleCategories(id: string): void {
    this.store.dispatch(toggleMemberActiveCoverageCategories({ itemId: id }));
  }
}
